import { createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'

import { api } from 'src/Services/api'
import { TransferwiseRequirement } from 'src/Types/payments'
import { APP } from 'src/Configs/App'
import { useAppSelector } from 'src/Hooks/redux'

import { NameSpace } from '../types'
import { getUserRole } from '../auth-process/credentialsSlice/selectors'

interface WiseAccountRequirementsData {
  data: Array<TransferwiseRequirement>
}
export const getTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  string
>(
  `${NameSpace.Wise}/getTransferwiseAccountRequirements`,
  async (currency, { rejectWithValue }) => {
    try {
      const { data } = await api.get<WiseAccountRequirementsData>(
        `${APP.TIKTOK_SERVER}/payout/transferwise/account-requirements/${currency}`,
      )
      return data.data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const getCreatorTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  string
>(
  `${NameSpace.Wise}/getCreatorTransferwiseAccountRequirements`,
  async (currency, { rejectWithValue }) => {
    try {
      const { data } = await api.get<Array<TransferwiseRequirement>>(
        `${APP.TIKTOK_SERVER}/creator/payouts/wise/recipients/requirements/${currency}`,
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const getCuratorTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  string
>(
  `${NameSpace.Wise}/getCuratorTransferwiseAccountRequirements`,
  async (currency, { rejectWithValue }) => {
    try {
      const { data } = await api.get<Array<TransferwiseRequirement>>(
        `${APP.TIKTOK_SERVER}/curator/payouts/wise/recipients/requirements/${currency}`,
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const useGetRequirements =
  (): typeof getTransferwiseAccountRequirements => {
    const role = useAppSelector(getUserRole)
    const isNewWise = APP.NEW_WISE_PROCESS_ENABLED

    if (!isNewWise) {
      return getTransferwiseAccountRequirements
    }

    if (role === 'influencer') {
      return getCreatorTransferwiseAccountRequirements
    }
    return getCuratorTransferwiseAccountRequirements
  }

interface SpecifyTransferwiseAccountBody {
  currency: string
  type: string
  details: Record<string, string | Record<string, string>>
}

export const specifyTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  SpecifyTransferwiseAccountBody
>(
  `${NameSpace.Wise}/specifyTransferwiseAccountRequirements`,
  async (body, { rejectWithValue }) => {
    try {
      const { data } = await api.post<WiseAccountRequirementsData>(
        `${APP.TIKTOK_SERVER}/payout/transferwise/account-requirements/quote`,
        body,
      )
      return data.data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const specifyCreatorTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  SpecifyTransferwiseAccountBody
>(
  `${NameSpace.Wise}/specifyCreatorTransferwiseAccountRequirements`,
  async (body, { rejectWithValue }) => {
    try {
      const { data } = await api.post<Array<TransferwiseRequirement>>(
        `${APP.TIKTOK_SERVER}/creator/payouts/wise/recipients/requirements/check`,
        body,
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const specifyCuratorTransferwiseAccountRequirements = createAsyncThunk<
  Array<TransferwiseRequirement>,
  SpecifyTransferwiseAccountBody
>(
  `${NameSpace.Wise}/specifyCuratorTransferwiseAccountRequirements`,
  async (body, { rejectWithValue }) => {
    try {
      const { data } = await api.post<Array<TransferwiseRequirement>>(
        `${APP.TIKTOK_SERVER}/curator/payouts/wise/recipients/requirements/check`,
        body,
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data?.message,
        code: response?.status,
      })
    }
  },
)

export const useSpecifyRequirements =
  (): typeof specifyTransferwiseAccountRequirements => {
    const role = useAppSelector(getUserRole)
    const isNewWise = APP.NEW_WISE_PROCESS_ENABLED

    if (!isNewWise) {
      return specifyTransferwiseAccountRequirements
    }
    if (role === 'influencer') {
      return specifyCreatorTransferwiseAccountRequirements
    }
    return specifyCuratorTransferwiseAccountRequirements
  }
